import { format, parseISO } from 'date-fns';

/**
 * Date format patterns enum
 */
enum DateFormat {
  /** ISO 8601 format: 2023-12-25 */
  ISO = 'yyyy-MM-dd',
  /** US format: 12/25/2023 */
  US = 'MM/dd/yyyy',
  /** European format: 25/12/2023 */
  EU = 'dd/MM/yyyy',
  /** Short month: Dec 25, 2023 */
  SHORT_MONTH = 'MMM dd, yyyy',
  /** Full month: December 25, 2023 */
  FULL_MONTH = 'MMMM dd, yyyy',
  /** Day first short: 25 Dec 2023 */
  DAY_FIRST_SHORT = 'dd MMM yyyy',
  /** With time: 2023-12-25 14:30 */
  ISO_WITH_TIME = 'yyyy-MM-dd HH:mm',
  /** US with time: 12/25/2023 02:30 PM */
  US_WITH_TIME = 'MM/dd/yyyy hh:mm a',
}

/**
 * Format a date string or Date object using date-fns
 * @param date - The date to format
 * @param formatString - The format string or DateFormat enum (default: DateFormat.SHORT_MONTH)
 * @returns Formatted date string
 */
function formatDate(
  date: string | Date,
  formatString: string | DateFormat = DateFormat.SHORT_MONTH
): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // Parse as ISO date but treat as local date (not UTC)
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      // For other date strings, use parseISO for proper parsing
      dateObj = parseISO(date);
    }
  } else {
    dateObj = date;
  }

  return format(dateObj, formatString);
}

/**
 * Format date and time to locale string
 * @param dateString - The date string to format
 * @returns Formatted date and time string
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format time ago (e.g., "2 hours ago", "3 days ago")
 * @param dateString - The date string to compare
 * @returns Human-readable time ago string
 */
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));
  const months = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));
  const years = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365));

  if (years > 0) {
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  } else if (months > 0) {
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  } else if (weeks > 0) {
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  } else if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
}

// Test function to verify date parsing behavior
function testDateParsing() {
  console.log('Testing date parsing:');
  console.log('Input: "2025-09-03"');
  console.log('Parsed as local date:', new Date(2025, 8, 3)); // month is 0-indexed
  console.log('Parsed as UTC:', new Date('2025-09-03'));
  console.log('Formatted result:', formatDate('2025-09-03', DateFormat.US));
}

// Uncomment to test: testDateParsing();

// Namespace export for convenient usage: dateUtil.formatDate()
export const dateUtil = {
  formatDate,
  formatDateTime,
  formatTimeAgo,
  DateFormat,
} as const;

